import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { usePortfolio } from "../../context/PortfolioContext";
import { Button } from "../../components/ui/button";
import { useToast } from "../../hooks/use-toast";
import { Eye, EyeOff, Save, Loader2, Users, Briefcase, BookOpen } from "lucide-react";

const ROW_DEFS = [
  {
    key: "membersPageVisibility",
    label: "Members Page",
    description:
      "Per-member units, capital, and contribution breakdown.",
    icon: Users,
  },
  {
    key: "portfolioPageVisibility",
    label: "Portfolio Page",
    description:
      "Holdings (equities + mutual funds), invested vs present value, P&L.",
    icon: Briefcase,
  },
  {
    key: "ledgerPageVisibility",
    label: "Ledger Page",
    description:
      "Multi-account assets/liabilities ledger with reconciliation.",
    icon: BookOpen,
  },
];

export default function PageAccess() {
  const { toast } = useToast();
  const { settings, reloadSettings } = usePortfolio() || {};
  const [draft, setDraft] = useState({
    membersPageVisibility: "all",
    portfolioPageVisibility: "all",
    ledgerPageVisibility: "all",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setDraft({
        membersPageVisibility: settings.membersPageVisibility || "all",
        portfolioPageVisibility: settings.portfolioPageVisibility || "all",
        ledgerPageVisibility: settings.ledgerPageVisibility || "all",
      });
    }
  }, [settings]);

  const dirty =
    settings &&
    (draft.membersPageVisibility !== settings.membersPageVisibility ||
      draft.portfolioPageVisibility !== settings.portfolioPageVisibility ||
      draft.ledgerPageVisibility !== (settings.ledgerPageVisibility || "all"));

  const setVal = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.updateSettings(draft);
      if (reloadSettings) await reloadSettings();
      toast({ title: "Saved", description: "Page access updated." });
    } catch (e) {
      toast({
        title: "Failed to save",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="page-access-root">
      <div>
        <h2 className="text-lg font-semibold text-white">Page Access</h2>
        <p className="text-sm text-slate-400 mt-1">
          Choose who can see specific pages. Admins always see every page —
          this only affects what regular members can navigate to.
        </p>
      </div>

      <div className="space-y-3">
        {ROW_DEFS.map((row) => {
          const value = draft[row.key];
          const Icon = row.icon;
          return (
            <div
              key={row.key}
              className="bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-5"
              data-testid={`page-access-row-${row.key}`}
            >
              <div className="flex items-start gap-4 flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center text-emerald-400">
                    <Icon size={16} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">
                      {row.label}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5 max-w-md">
                      {row.description}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 self-stretch md:self-auto">
                  <button
                    type="button"
                    onClick={() => setVal(row.key, "all")}
                    data-testid={`page-access-${row.key}-all`}
                    className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                      value === "all"
                        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
                        : "bg-slate-900 text-slate-400 border-slate-700 hover:text-white"
                    }`}
                  >
                    <Eye size={14} /> All members
                  </button>
                  <button
                    type="button"
                    onClick={() => setVal(row.key, "admin")}
                    data-testid={`page-access-${row.key}-admin`}
                    className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                      value === "admin"
                        ? "bg-amber-500/15 text-amber-400 border-amber-500/40"
                        : "bg-slate-900 text-slate-400 border-slate-700 hover:text-white"
                    }`}
                  >
                    <EyeOff size={14} /> Admin only
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="text-xs text-slate-500">
          {dirty ? "Unsaved changes" : "All changes saved"}
        </div>
        <Button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="page-access-save-btn"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950"
        >
          {saving ? (
            <>
              <Loader2 size={14} className="mr-2 animate-spin" /> Saving…
            </>
          ) : (
            <>
              <Save size={14} className="mr-2" /> Save changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
